import {
  Button,
  Cell,
  Column,
  MenuItem,
  MenuTrigger,
  Row,
  Table,
  TableBody,
  TableHeader,
} from '@eleveo/components-react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BinIcon,
  CheckmarkRoundedIcon,
  CircleIcon,
  PencilIcon,
  ThreeDotsIcon,
} from '@eleveo/icons-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULTS_SCHEDULE_ELEMENTS, NOT_DELETABLE_OR_EDITABLE_SCHEDULE_ELEMENTS } from './config';
import { LIST_OF_SCHEDULE_ELEMENTS_CONTAINER_TAG, getScheduleElementPropTag } from './constants';
import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { translateScheduleElementName } from '../../components/MySchedule/transformData';
import { formatSecondsToReadableForm } from '../../utils/dateRange';

import type { ScheduleElement } from '../../graphql/components';
import type { ColumnProps } from '@eleveo/components-react';

export interface ScheduleElementsTableProps {
  activeId?: string;
  onDeleteScheduleElement: (scheduleElement: ScheduleElement) => void;
  onDisplayOrderMove: (scheduleId: string, newDisplayOrderPosition: number) => void;
  onEditScheduleElement: (scheduleElement: ScheduleElement) => void;
  scheduleElements: ScheduleElement[];
}

export const ScheduleElementsTable: React.FC<ScheduleElementsTableProps> = ({
  activeId,
  onDeleteScheduleElement,
  onDisplayOrderMove,
  onEditScheduleElement,
  scheduleElements,
}) => {
  const { userHasPermissions } = useAuth();
  const { t } = useTranslation();

  const hasEditPermission = userHasPermissions([Permissions.EditScheduleElements]);

  const columns: (ColumnProps<unknown> & { key: React.Key })[] = [
    {
      key: 'color',
      children: t('scheduleElements.color'),
      width: 55,
    },
    {
      key: 'name',
      width: 200,
      children: t('form.name'),
    },
    {
      key: 'defaultDuration',
      align: 'end',
      children: t('scheduleElements.defaultDuration'),
    },
    {
      key: 'tags',
      children: t('scheduleElements.contributesToTag'),
    },
    {
      key: 'paid',
      align: 'center',
      children: t('schedule.element.paid'),
    },
    {
      key: 'productive',
      align: 'center',
      children: t('schedule.element.productive'),
    },
    {
      key: 'showInOverview',
      align: 'center',
      children: t('scheduleElements.showInOverview'),
    },
    {
      key: 'showInOverviewBig',
      align: 'center',
      children: t('scheduleElements.showInOverviewBig'),
    },
    {
      key: 'shift',
      align: 'center',
      children: t('shiftTemplate.shift'),
    },
    {
      key: 'vacation',
      align: 'center',
      children: t('schedule.element.default.Vacation'),
    },
    {
      key: 'paidTimeOff',
      align: 'center',
      children: t('schedule.element.paidTimeOff'),
    },
    {
      key: 'sick',
      align: 'center',
      children: t('schedule.element.default.Sick'),
    },
    {
      key: 'pinnable',
      align: 'center',
      children: t('scheduleElements.pinnable'),
    },
    // Action column hides if no permissions to edit
    ...(hasEditPermission
      ? [
          {
            key: 'actions',
            children: t('layout.action.actions'),
            isActions: true,
            hideHeader: true,
          },
        ]
      : []),
  ];

  const rows = useMemo(() => {
    return (
      scheduleElements.map((scheduleElement) => {
        const actionsItems = [
          hasEditPermission && {
            icon: <PencilIcon />,
            id: '1',
            isDisabled: NOT_DELETABLE_OR_EDITABLE_SCHEDULE_ELEMENTS.includes(scheduleElement.name),
            onClick: () => onEditScheduleElement(scheduleElement),
            testId: getScheduleElementPropTag('edit', scheduleElement.id),
            text: t('layout.action.edit'),
          },
          hasEditPermission && {
            icon: <ArrowUpIcon />,
            id: '2',
            isDisabled: scheduleElement.displayOrder === 1,
            onClick: () => onDisplayOrderMove(scheduleElement.id, scheduleElement.displayOrder - 1),
            testId: getScheduleElementPropTag('move-up', scheduleElement.id),
            text: t('layout.action.moveUp'),
          },
          hasEditPermission && {
            icon: <ArrowDownIcon />,
            id: '3',
            isDisabled: scheduleElement.displayOrder === scheduleElements.length,
            onClick: () => onDisplayOrderMove(scheduleElement.id, scheduleElement.displayOrder + 1),
            testId: getScheduleElementPropTag('move-down', scheduleElement.id),
            text: t('layout.action.moveDown'),
          },
          hasEditPermission && {
            id: '4',
            options: [
              {
                icon: <BinIcon />,
                id: '1',
                isDisabled: DEFAULTS_SCHEDULE_ELEMENTS.includes(scheduleElement.name),
                onClick: () => onDeleteScheduleElement(scheduleElement),
                testId: getScheduleElementPropTag('delete', scheduleElement.id),
                text: t('layout.action.delete'),
              },
            ],
          },
        ].filter(Boolean) as MenuItem[];

        const firstTag = scheduleElement.tags?.[0];

        return {
          actions: (
            <MenuTrigger items={actionsItems}>
              <Button
                aria-label={t('layout.action.actions')}
                isSmall
                testId={getScheduleElementPropTag('actions', scheduleElement.id)}
                variant="quiet"
              >
                <ThreeDotsIcon />
              </Button>
            </MenuTrigger>
          ),
          color: (
            <CircleIcon
              color={`#${[scheduleElement.color.red, scheduleElement.color.green, scheduleElement.color.blue]
                .map((value) => value.toString(16).padStart(2, '0'))
                .join('')}`}
              fontSize={16}
            />
          ),
          name: <strong>{translateScheduleElementName(t, scheduleElement)}</strong>,
          defaultDuration: <strong>{formatSecondsToReadableForm(scheduleElement.defaultDuration, t, true)} </strong>,
          tags: <strong>{firstTag?.value ?? null}</strong>,
          paid: scheduleElement.paid ? <CheckmarkRoundedIcon color="#5B5E5E" fontSize={16} /> : null,
          key: String(scheduleElement.id),
          productive: scheduleElement.productive ? <CheckmarkRoundedIcon color="#5B5E5E" fontSize={16} /> : null,
          showInOverview: scheduleElement.showInOverview ? (
            <CheckmarkRoundedIcon color="#5B5E5E" fontSize={16} />
          ) : null,
          showInOverviewBig: scheduleElement.showInOverviewBig ? (
            <CheckmarkRoundedIcon color="#5B5E5E" fontSize={16} />
          ) : null,
          shift: scheduleElement.shift ? <CheckmarkRoundedIcon color="#5B5E5E" fontSize={16} /> : null,
          vacation: scheduleElement.vacation ? <CheckmarkRoundedIcon color="#5B5E5E" fontSize={16} /> : null,
          paidTimeOff: scheduleElement.paidTimeOff ? <CheckmarkRoundedIcon color="#5B5E5E" fontSize={16} /> : null,
          sick: scheduleElement.sick ? <CheckmarkRoundedIcon color="#5B5E5E" fontSize={16} /> : null,
          pinnable: scheduleElement.pinnable ? <CheckmarkRoundedIcon color="#5B5E5E" fontSize={16} /> : null,
        };
      }) ?? []
    );
  }, [scheduleElements, t, onDisplayOrderMove, onEditScheduleElement, onDeleteScheduleElement, hasEditPermission]);

  return (
    <Table
      aria-label={t('scheduleElements.scheduleElements', { count: 2 })}
      className="h-full overflow-auto"
      highlightedKey={activeId}
      isStickyHeader
      testId={LIST_OF_SCHEDULE_ELEMENTS_CONTAINER_TAG}
    >
      <TableHeader columns={columns}>{(column) => <Column {...column} key={column.key} />}</TableHeader>

      <TableBody items={rows}>
        {(item) => <Row key={item.key}>{(columnKey) => <Cell>{item[columnKey]}</Cell>}</Row>}
      </TableBody>
    </Table>
  );
};
